import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useIamPermission, usePermissions } from "@/features/auth/hooks/usePermissions";
import { cancelAskNexusReports, sendAskNexusMessage, startAskNexusReport, stopAskNexusAnswer } from "@/features/ask-nexus/api/ask-nexus.api";
import { useAskNexusPanel } from "@/features/ask-nexus/store/ask-nexus-panel.store";
import { askNexusAvailabilityQueryOptions, askNexusKeys } from "@/lib/queries/ask-nexus.queries";

export interface AskNexusAccess {
  canView: boolean;
  canSend: boolean;
  canStop: boolean;
  /** Turn a confirmed report request into a PowerPoint (pending security review; off unless granted). */
  canGenerateReport: boolean;
}

/**
 * Two separate checks, both needed: the environment runs Ask Nexus, and the
 * caller may use it there. The second is the rule the API applies to every Ask
 * Nexus route: the key granted on the active organization, or SUPER_TENANT
 * membership. UI gating only; the server enforces both again.
 */
export function useAskNexusAccess(): AskNexusAccess {
  const permissions = usePermissions();
  const isGranted = useIamPermission(permissions.ASK_NEXUS_VIEW_PAGE);
  const isSendGranted = useIamPermission(permissions.ASK_NEXUS_SEND_MESSAGE);
  const isStopGranted = useIamPermission(permissions.ASK_NEXUS_STOP_ANSWER);
  const isReportGranted = useIamPermission(permissions.ASK_NEXUS_GENERATE_REPORT);
  const availability = useQuery(askNexusAvailabilityQueryOptions(isGranted));
  const canView = isGranted && availability.data?.isAvailable === true;
  return { canView, canSend: canView && isSendGranted, canStop: canView && isStopGranted, canGenerateReport: canView && isReportGranted };
}

/**
 * Sends a message. If the panel closed while the send was on its way, the answer it started is stopped once the send
 * returns, for whoever may stop answers, even when the panel has reopened since: the close itself could not see
 * that answer running yet. The Stop names that answer, so a newer one keeps going.
 */
export function useSendAskNexusMessage(tenantId: string, { canStop }: { canStop: boolean }) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: Parameters<typeof sendAskNexusMessage>[0]) => {
      const closeCountAtSend = useAskNexusPanel.getState().closeCount;
      const sent = await sendAskNexusMessage(input);
      const hasClosedSince = useAskNexusPanel.getState().closeCount !== closeCountAtSend;
      if (canStop && hasClosedSince && sent.messageId) {
        // The send itself succeeded; a Stop that fails only leaves this answer to its own deadline.
        await stopAskNexusAnswer(sent.conversationId, sent.messageId).catch(() => undefined);
      }
      return sent;
    },
    meta: { skipGlobalError: true },
    onSuccess: ({ conversationId }) => {
      // A reply that lands after an organization switch must not open in the other organization.
      if (useAskNexusPanel.getState().tenantId === tenantId) {
        useAskNexusPanel.getState().openConversation(conversationId);
      }
      void queryClient.invalidateQueries({ queryKey: askNexusKeys.tenant(tenantId) });
    },
  });
}

export function useStopAskNexusAnswer(tenantId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (conversationId: string) => stopAskNexusAnswer(conversationId),
    meta: { skipGlobalError: true },
    onSettled: (_result, _error, conversationId) => {
      void queryClient.invalidateQueries({ queryKey: askNexusKeys.conversation(tenantId, conversationId) });
    },
  });
}

/**
 * Starts the report a request card names, then reads the conversation again so the card carries it. If the panel
 * closed while the start was on its way, that request is cancelled once the start returns, even when the panel has
 * reopened since: the close's own cancel may have reached Nexus before the report existed. The cancel names only
 * this request, so a report started after reopening is untouched.
 */
export function useStartAskNexusReport(tenantId: string, conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (requestId: string) => {
      const closeCountAtStart = useAskNexusPanel.getState().closeCount;
      const report = await startAskNexusReport(conversationId, requestId);
      if (useAskNexusPanel.getState().closeCount !== closeCountAtStart) await cancelAskNexusReports(conversationId, [requestId]);
      return report;
    },
    meta: { skipGlobalError: true },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: askNexusKeys.conversation(tenantId, conversationId) });
    },
  });
}
